import React from "react";
import _ from "lodash";
import {
  Button,
  Box,
  SelectOption,
  Select,
  Input,
  TextArea,
  Text,
  Icon,
} from "@showitinc/showit-wc";

import DesignExportStore from "../stores/DesignExportStore";
import UIActions from "../actions/UIActions";
import ExportActions from "../actions/ExportActions";
import UserActions from "../actions/UserActions";
import AppConstants from "../constants/AppConstants";
import MessageUtils from "../utils/MessageUtils";
import ValidationUtils from "../utils/ValidationUtils";
import SubviewError from "./SubviewError";
import SubviewProcessing from "./SubviewProcessing";
import Modal from "./base/Modal";
import ButtonBar from "./base/ButtonBar";
import { copyToClipboard } from "../utils/AppUtils";
import uiPrefix from "../utils/UiPrefixUtils";
import { formatPropUpdate } from "../utils/PropertyUtils";
import styles from "./DesignExportModal.module.css";
import {
  EXPORT_VIEW_COMPLETE,
  EXPORT_VIEW_ERROR,
  EXPORT_VIEW_INITIAL,
  EXPORT_VIEW_LOADING,
  EXPORT_VIEW_PROCESSING,
} from "@/constants/ExportViewConstants.mjs";
import ProtorendererToggle from "@/app2components/modals/ProtorendererToggle";

/** @typedef {import("@/stores/DesignExportStore").DesignExportState} DesignExportState */

class DesignExportModal extends React.Component {
  /** @type {DesignExportState} */
  state = DesignExportStore.getInitialState();

  constructor() {
    super();
    let footerContent = "";
  }

  componentDidMount() {
    this.unsubscribeStore = DesignExportStore.listen(this._onStoreChange);
  }

  componentWillUnmount() {
    if (this.unsubscribeStore) {
      _.attempt(this.unsubscribeStore);
    }
  }

  /**
   *
   * @param {DesignExportState} state
   */
  _onStoreChange = (state) => {
    this.setState(state);
  };

  _exportDesign = () => {
    const { design } = this.state;

    if (_.isEmpty(design.name)) {
      this.setState({ error: "Name cannot be empty" });
      return;
    }

    ExportActions.exportDesign(design);
    UserActions.trackEvent({ event: "export", meta: design });
  };

  _changeValue = (propInfo) => {
    const { state } = this;
    const { name, value } = propInfo;
    _.set(state, name, value);

    this.setState(state);
  };

  _changeExportKey = (propInfo) => {
    const { state } = this;
    const { designExports, origName } = state;
    const selectedExport = designExports.find(
      (design) => String(design.export_id) === String(propInfo.value),
    );
    const { name } = propInfo;
    // Store the canonical numeric export_id — the store deletes any
    // non-number exportId before the POST, so the DOM's string value
    // would silently force a fresh share key.
    _.set(state, name, selectedExport ? selectedExport.export_id : null);

    if (selectedExport) {
      _.set(state, "design.name", selectedExport.name);
      _.set(state, "design.desc", selectedExport.desc == null ? "" : selectedExport.desc);
    } else if (_.isEmpty(propInfo.value)) {
      _.set(state, "design.name", origName);
      _.set(state, "design.desc", "");
    }

    this.setState(state);
  };

  _copyExportKey = async (key) => {
    await copyToClipboard({
      val: key,
      showToast: true,
    });
  };

  _validateStr = (data) => {
    const result = {
      valid: true,
      message: "",
    };
    const { label, value } = data;

    if (ValidationUtils.isEmptyStr(value)) {
      result.valid = false;
      result.message = `${label} cannot be empty`;
    }

    return result;
  };

  _close = () => {
    UIActions.modalClose(AppConstants.MODAL_DESIGN_EXPORT);
  };

  _getDesignExportOptions = (designExports) => {
    const options = [];

    if (designExports != null && _.size(designExports) > 0) {
      _.forEach(designExports, (designExport) => {
        const { key, export_id, name } = designExport;
        options.push(
          <option key={export_id} value={export_id}>
            {key}&nbsp;&nbsp;&nbsp;&nbsp;::&nbsp;&nbsp;&nbsp;&nbsp;{name}
          </option>,
        );
      });
    }

    options.push(
      <option key="new" value="">
        Create New Share Key
      </option>,
    );

    return options;
  };

  _getInitialView = () => {
    const { design, designExports, error } = this.state;

    const exportId = this.state?.design?.exportId || "";
    const { typeDesc, name, desc } = design;
    const designName = name;
    const designDesc = desc;

    this.footerContent = (
      <ButtonBar>
        <Box mr="3">
          <Button
            variant="minimal"
            data-e2e={uiPrefix("modal-cancel-btn")}
            className={uiPrefix("modal-cancel-btn")}
            onClick={this._close}
          >
            Cancel
          </Button>
        </Box>
        <Button
          variant="feature"
          data-e2e={uiPrefix("modal-share-btn")}
          className={uiPrefix("modal-share-btn")}
          onClick={this._exportDesign}
        >
          Share
        </Button>
      </ButtonBar>
    );

    return (
      <div className={styles["panel-inner-wrapper"]}>
        <Box>
          <Select
            value={`${exportId}`}
            longLabel
            label="Share Key"
            onSISelect={(e) =>
              this._changeExportKey(formatPropUpdate("design.exportId", e.target.value))
            }
          >
            {designExports?.map((designExport) => {
              const { key, export_id, name } = designExport;
              return (
                <SelectOption key={export_id} value={export_id}>
                  {key} :: {name}
                </SelectOption>
              );
            })}
            <SelectOption key="new" value="">
              Create New Share Key
            </SelectOption>
          </Select>

          <Input
            label={`${typeDesc} Name`}
            longLabel
            value={designName}
            onFocus={() => this.setState({ error: "" })}
            onSIInput={(e) => this._changeValue(formatPropUpdate("design.name", e.target.value))}
          />

          <Box ml="3">
            <Text style={{ color: "var(--text-status-destruct)" }}>{error}</Text>
          </Box>
          <TextArea
            longLabel
            label={`${typeDesc} Description`}
            value={designDesc}
            resize
            onSIInput={(e) => this._changeValue(formatPropUpdate("design.desc", e.target.value))}
          />
          <Box pt="6">
            <ProtorendererToggle showDescription={false} />
          </Box>
        </Box>
        <Box>
          <Box
            py="3"
            pl="3"
            d="flex"
            align="center"
            mb="3"
            style={{ borderBottom: "1px solid #333" }}
          >
            <Icon type="infoOutline" size="16" />
            <Box pl="1" ml="2">
              <Text>
                Please be sure you have the right to distribute any media or fonts used with your
                design.
              </Text>
            </Box>
          </Box>
        </Box>
      </div>
    );
  };

  _getProcessingView = (message) => <SubviewProcessing message={message} />;

  _getCompleteView = () => {
    const { design } = this.state;
    const { typeDesc, exportKey } = design;

    this.footerContent = (
      <ButtonBar>
        <Box>
          <Button
            variant="feature"
            data-e2e={uiPrefix("modal-close-btn")}
            className={uiPrefix("modal-close-btn")}
            onClick={this._close}
          >
            Done
          </Button>
        </Box>
      </ButtonBar>
    );

    return (
      <div className={styles["complete-panel-wrapper"]}>
        <Box
          d="flex"
          flex="col"
          mb="2"
          justify="center"
          align="center"
          className={styles["complete-panel-inner-wrapper"]}
        >
          <Box mb="2">
            <Text variant="title-hero">{MessageUtils.getSuccessExpression()}!</Text>
          </Box>
          <Box mb="2">
            <Text variant="subtitle">Your {typeDesc} has been made available to share.</Text>
          </Box>
          <div
            className={styles["export-key-container"]}
            onClick={() => this._copyExportKey(exportKey)}
            title="Copy Share Key"
          >
            <Text variant="title-page">{exportKey}</Text>
            <Icon type="copy" size="16" />
          </div>
          <Box mb="2">
            <Text variant="small">The Share Key can be used to import this design into Showit</Text>
          </Box>
        </Box>
      </div>
    );
  };

  /**
   * Renders error view
   * @param {DesignExportState["process"]} process
   * @returns {React.ReactElement}
   */
  _getErrorView = (process) => {
    const error = _.get(process, "error", false);
    return <SubviewError error={error} onClose={this._close} />;
  };

  render() {
    const { show } = this.props;
    const { process } = this.state;
    const { view } = process;
    const modalProps = _.pick(this.props, Modal.BASIC_PROPS);

    let currentView = null;

    if (show) {
      switch (view) {
        case EXPORT_VIEW_PROCESSING:
          currentView = this._getProcessingView("Preparing Design");
          break;
        case EXPORT_VIEW_COMPLETE:
          currentView = this._getCompleteView();
          break;
        case EXPORT_VIEW_ERROR:
          currentView = this._getErrorView(process);
          break;
        case EXPORT_VIEW_INITIAL:
          currentView = this._getInitialView();
          break;
        case EXPORT_VIEW_LOADING:
          currentView = this._getProcessingView("Loading");
          break;
        default:
          break;
      }
    }

    return (
      <Modal {...modalProps} backdrop="static">
        <Modal.Dialog>
          <Modal.Dialog.Header>
            <Modal.Dialog.Title>Share A Design</Modal.Dialog.Title>
          </Modal.Dialog.Header>
          <Modal.Dialog.Body className={styles["modal-body"]}>
            <div className={styles["panel-wrapper"]}>{currentView}</div>
          </Modal.Dialog.Body>
          <Modal.Dialog.Footer>{this.footerContent}</Modal.Dialog.Footer>
        </Modal.Dialog>
      </Modal>
    );
  }
}

export default DesignExportModal;
